import React, { useMemo, useRef, useState } from 'react';
import { Box, Stack, TextField, Typography, Chip, Paper, Button, Tooltip, Dialog, DialogTitle, DialogContent, Autocomplete, Snackbar, Alert } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import ArticleIcon from '@mui/icons-material/Article';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SearchIcon from '@mui/icons-material/Search';
import EditorTemplate from '../document-editor/Editor';
import { useTemplateImport } from './ImportReviewDialog';

const SAMPLE = {
  FirstName: 'Arben',
  LastName: 'Shala',
  ParentName: 'Ilir',
  PersonalId: '123456789',
  Address: 'Rr. Prishtina 10',
  Phone: '+383 44 000 000',
  PostalCode: '10000',
  BankAccount: 'XK051234...',
  BankName: 'Banka Demo',
  Birthday: '1990-05-10',
  Country: 'Kosovë',
  Place: 'Prishtinë',
  Email: 'arben.shala@example.com',
  Gender: 'M',
  MaritalStatus: 'Single',
  Qualification: 'Bachelor',
  Profession: 'Matematikan',
  ContractType: 'Kohë e caktuar',
  StartDate: '2025-01-01',
  EndDate: '2025-12-31',
  WorkType: 'Full-time',
  SalaryType: 'Mujore',
  Department: 'IT',
  BaseSalary: '600',
  BrutoNeto: 'Bruto',
  HourSalary: '3.5',
  HourSalaryWeekend: '4.5',
  HourSalaryHoliday: '5.0',
  TermsOfContract: '—',
  CompanyName: 'ATA BOOKKEEPING SH.P.K.',
  CompanyNUI: '810184119',
  CompanyFiscalNumber: '811223344',
  CompanyAddress: 'Rruga Shaqir Igrishta, Nr. 7, Prishtinë',
  CompanyCity: 'Prishtinë',
  CompanyCountry: 'Kosovë',
  CompanyPhone: '+383 38 224 455',
  CompanyEmail: 'hr@ata-bk.com',
  CompanyWebsite: 'www.ata-bk.com',
  CompanyBankName: 'NLB Banka',
  CompanyBankAccount: 'XK00 0000 0000 0000 0000',
  CompanyDirector: 'Fisnik Sejdiu, Drejtor',
  CompanyLogo: '',
};

function replaceTokens(html, source) {
  if (!html) return '';
  let out = html;
  for (const [k, v] of Object.entries(source || {})) {
    const re = new RegExp('\\@' + k + '(?![A-Za-z0-9_])', 'g');
    out = out.replace(re, v ?? '');
  }
  return out;
}

export default function TemplateDesigner({ value, onChange, tags = [] }) {
  const [filter, setFilter] = useState('');
  const editorRef = useRef(null);
  const [openPalette, setOpenPalette] = useState(false);
  const [paletteValue, setPaletteValue] = useState(null);

  // Normalize tags: accept ["FirstName", ...] or [{ key: 'FirstName', label: 'Emri' }, ...]
  const normTags = useMemo(() => {
    return (tags || []).map((t) =>
      typeof t === 'string' ? { key: t, label: t } : { key: t.key, label: t.label || t.key }
    );
  }, [tags]);

  const filteredTags = useMemo(() => {
    const f = (filter || '').toLowerCase();
    if (!f) return normTags;
    return normTags.filter((t) =>
      (t.label || '').toLowerCase().includes(f) || (t.key || '').toLowerCase().includes(f)
    );
  }, [filter, normTags]);

  // Categorize tags for easier browsing
  const CATS = useMemo(() => ({
    worker: new Set(['FirstName','LastName','ParentName','PersonalId','Birthday','Address','Phone','Email','Department','Qualification','Profession','WorkType','StartDate','EndDate','SalaryType','BrutoNeto','BaseSalary','HourSalary','HourSalaryWeekend','HourSalaryHoliday','TermsOfContract']),
    company: new Set(['CompanyName','CompanyNUI','CompanyFiscalNumber','CompanyAddress','CompanyCity','CompanyCountry','CompanyPhone','CompanyEmail','CompanyWebsite','CompanyBankName','CompanyBankAccount','CompanyDirector','CompanyLogo']),
    contract: new Set(['ContractType'])
  }), []);
  const byCategory = useMemo(() => {
    const res = { worker: [], company: [], contract: [], other: [] };
    (filteredTags || []).forEach((t) => {
      const k = t.key;
      if (CATS.worker.has(k)) res.worker.push(t);
      else if (CATS.company.has(k)) res.company.push(t);
      else if (CATS.contract.has(k)) res.contract.push(t);
      else res.other.push(t);
    });
    return res;
  }, [filteredTags, CATS]);

  const handleInsert = (tag) => {
    if (editorRef.current) {
      const token = typeof tag === 'string' ? tag : tag.key;
      editorRef.current.insertContent(`@${token}`);
      editorRef.current.focus();
    }
  };

  const insertPageBreak = () => editorRef.current?.insertContent('<div class="page-break"></div>');
  const insertHeaderMeta = () => editorRef.current?.insertContent(`
<div class="contract-header">
  <div class="logo">@CompanyLogo</div>
  <div>
    <div class="title">@CompanyName</div>
    <div class="subtitle">@CompanyAddress — @CompanyCity, @CompanyCountry · NUI: @CompanyNUI</div>
  </div>
</div>
<div class="text-center"><h1>Titulli i kontratës</h1></div>
<div class="meta">
  <div class="label">Emri dhe mbiemri</div><div class="value">@FirstName @LastName</div>
  <div class="label">Numri personal</div><div class="value">@PersonalId</div>
  <div class="label">Pozita</div><div class="value">@WorkType — @Department</div>
  <div class="label">Periudha</div><div class="value">@StartDate – @EndDate</div>
</div>
`);
  const insertArticleBox = () => editorRef.current?.insertContent(`
<table width="100%"><thead><tr><th style="width:50%">Neni 1</th><th>Neni 2</th></tr></thead><tbody>
<tr><td>
<p>Lorem ipsum dolor sit amet...</p>
</td><td>
<p>Morbi leo risus, porta ac consectetur ac...</p>
</td></tr></tbody></table>
`);

  const previewHtml = useMemo(() => replaceTokens(value || '', SAMPLE), [value]);

  const fileInputRef = useRef(null);

  const [notice, setNotice] = useState(null);
  const wordImport = useTemplateImport({
    onApplied: (html, { fields, blanks }) => {
      if (editorRef.current?.setContent) editorRef.current.setContent(html);
      else if (typeof onChange === 'function') onChange(html);
      setNotice({ severity: 'success', text: `Dokumenti u importua me ${fields} fusha dinamike${blanks ? ` · ${blanks} vende mbetën për plotësim me dorë` : ''}.` });
    },
    onError: (text) => setNotice({ severity: 'error', text }),
  });

  // keyboard shortcut to open palette (Ctrl+/)
  React.useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault(); setOpenPalette(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleChipClick = (t, e) => {
    if (e?.type === 'contextmenu') {
      e.preventDefault(); navigator.clipboard.writeText('@' + (t.key || t.label));
    } else {
      handleInsert(t);
    }
  };

  const handlePrint = () => {
    const win = window.open('', '_blank');
    if (!win) return;
    const linkCss = `<link rel="stylesheet" href="${process.env.PUBLIC_URL || ''}/contract-print.css" />`;
    const doc = `<!doctype html><html><head><meta charset="utf-8"/>${linkCss}<title>Print Preview</title></head><body><div class="contract">${previewHtml}</div></body></html>`;
    win.document.open();
    win.document.write(doc);
    win.document.close();
    win.focus();
    // Defer print a bit to allow rendering
    setTimeout(() => {
      win.print();
    }, 300);
  };

  const htmlToText = (html) => {
    if (!html) return '';
    let s = String(html);
    // simple block handling
    s = s.replace(/<\/(p|div|h[1-6]|li|tr)\s*>/gi, '\n');
    s = s.replace(/<br\s*\/?\s*>/gi, '\n');
    s = s.replace(/<li\b[^>]*>/gi, '- ');
    // strip all tags
    s = s.replace(/<[^>]+>/g, '');
    // decode entities
    const el = document.createElement('textarea');
    el.innerHTML = s;
    const text = el.value;
    return text.replace(/\n{3,}/g, '\n\n').trim();
  };

  const handleCopyTxt = (useSample = true) => {
    const src = useSample ? previewHtml : (value || '');
    const txt = htmlToText(src);
    navigator.clipboard.writeText(txt);
  };

  const CAT_LABEL = { worker: 'Punëtori', company: 'Kompania', contract: 'Kontrata', other: 'Tjera' };

  return (
    <Box>
      {/* Editori majtas, fushat djathtas. Më parë paneli i fushave rrinte mbi
          editor dhe e shtynte atë gjysmë ekrani poshtë — pikërisht aty ku
          shkruhet. Te ekranet e ngushta paneli kthehet sipër, por i kufizuar. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 290px' }, gap: 2, alignItems: 'start' }}>
        <Box sx={{ minWidth: 0 }}>
          <EditorTemplate
            name="Text"
            value={value}
            getBackData={onChange}
            onReady={(editor) => (editorRef.current = editor)}
            tags={normTags}
          />
          <Stack direction="row" spacing={0.5} useFlexGap sx={{ mt: 1 }} flexWrap="wrap">
            <Button size="small" startIcon={<NoteAddIcon />} onClick={insertHeaderMeta} sx={{ textTransform: 'none' }}>Kokë + të dhëna</Button>
            <Button size="small" startIcon={<ArticleIcon />} onClick={insertArticleBox} sx={{ textTransform: 'none' }}>Kuti me nene</Button>
            <Button size="small" startIcon={<HorizontalRuleIcon />} onClick={insertPageBreak} sx={{ textTransform: 'none' }}>Faqe e re</Button>
            <input ref={fileInputRef} type="file" accept=".docx" hidden onChange={(e)=> { wordImport.start(e.target.files?.[0]); e.target.value = ''; }} />
            <Button size="small" disabled={wordImport.busy} startIcon={<UploadFileIcon />} onClick={()=> fileInputRef.current?.click()} sx={{ textTransform: 'none', ml: { sm: 'auto' } }}>
              {wordImport.busy ? 'Duke lexuar…' : 'Importo nga Word'}
            </Button>
          </Stack>
        </Box>

        <Paper variant="outlined" sx={{
          order: { xs: -1, lg: 0 },
          position: { lg: 'sticky' }, top: { lg: 80 },
          borderRadius: 2, display: 'flex', flexDirection: 'column',
          maxHeight: { xs: 260, lg: 'calc(100vh - 140px)' }, overflow: 'hidden',
        }}>
          <Box sx={{ p: 1.5, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>Fushat dinamike</Typography>
              <Tooltip title="Kërko dhe fut një fushë (Ctrl+/)">
                <Button size="small" onClick={()=> setOpenPalette(true)} sx={{ textTransform: 'none', minWidth: 0, px: 1, fontSize: 12 }}>Ctrl+/</Button>
              </Tooltip>
            </Stack>
            <TextField
              placeholder="Kërko fushë…"
              size="small"
              fullWidth
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ color: 'text.disabled', mr: 0.75 }} /> }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' }, mt: 0.75, lineHeight: 1.4 }}>
              Kliko një fushë për ta futur te kursori. Në kontratë zëvendësohet me të dhënat e punëtorit.
            </Typography>
          </Box>
          <Box sx={{ p: 1.5, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {['worker','company','contract','other'].map((cat) => (
              byCategory[cat]?.length ? (
                <Box key={cat}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.75 }}>
                    {CAT_LABEL[cat]}
                  </Typography>
                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                    {byCategory[cat].map((t) => (
                      <Tooltip key={t.key} title={SAMPLE?.[t.key] ? `Shembull: ${SAMPLE[t.key]}` : `@${t.key}`} arrow disableInteractive>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t.label.replace(/^Kompania – /, '')}
                          onClick={(e) => handleChipClick(t, e)}
                          onContextMenu={(e)=> handleChipClick(t, e)}
                          sx={{
                            fontSize: 12, height: 24, borderRadius: 1.5,
                            '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'action.hover' },
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>
              ) : null
            ))}
            {filteredTags.length === 0 && (
              <Typography variant="body2" color="text.secondary">Asnjë fushë nuk përputhet.</Typography>
            )}
          </Box>
        </Paper>
      </Box>

      {wordImport.dialog}
      <Snackbar open={Boolean(notice)} autoHideDuration={6000} onClose={() => setNotice(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {notice ? <Alert severity={notice.severity} onClose={() => setNotice(null)} variant="filled">{notice.text}</Alert> : <span />}
      </Snackbar>

      {/* Palette dialog */}
      <Dialog open={openPalette} onClose={()=> setOpenPalette(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Fut fushë (@tag)</DialogTitle>
        <DialogContent>
          <Autocomplete
            autoFocus
            options={normTags}
            value={paletteValue}
            onChange={(_, v)=> setPaletteValue(v)}
            getOptionLabel={(t)=> t ? `@${t.label}` : ''}
            renderInput={(params)=> <TextField {...params} label="Kërko fushë" />}
            onKeyDown={(e)=>{
              if (e.key === 'Enter' && paletteValue) {
                handleInsert(paletteValue); setOpenPalette(false); setPaletteValue(null);
              }
            }}
          />
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={()=> setOpenPalette(false)}>Mbyll</Button>
            <Button variant="contained" onClick={()=>{ if (paletteValue) { handleInsert(paletteValue); setOpenPalette(false); setPaletteValue(null); } }}>Fut</Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
